import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  Home,
  Users,
  Calendar,
  ClipboardList,
  CheckSquare,
  FileText,
  DollarSign,
  Settings,
  LogOut,
  X,
  Plus,
  ClipboardCheck,
  AlertTriangle,
  Shield,
  Activity,
  Stethoscope,
  GitBranch,
  LockKeyhole,
  KeyRound,
  Calculator,
  MapPin,
  Route,
  Sparkles,
  BarChart3,
  MonitorUp,
  BellRing,
  ShieldCheck,
  TrendingDown,
  Building2,
  BriefcaseBusiness,
  Bot,
  Car,
  UserCog,
  Palette,
} from 'lucide-react';
import { cn } from '@/core/utils';
import { useAuth, usePermissions } from '@/core/hooks';
import { Button } from '@/core/components';
import type { Role } from '@/core/types/auth';
import { useProductUX } from './ProductUXProvider';
import { useCompanyProfile } from '@/app/utils/companyProfile';

export interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  section: 'Core' | 'Operations' | 'Care' | 'Finance' | 'Intelligence' | 'Governance' | 'SuperAdmin' | 'System';
  permission?: string;
  requiredRole?: Role;
  requiredRoles?: Role[];
}

const navItems: NavItem[] = [
  { label: 'Dashboard', path: '/', icon: <Home className="h-5 w-5" />, section: 'Core' },
  {
    label: 'Clients',
    path: '/clients',
    icon: <Users className="h-5 w-5" />,
    section: 'Core',
    permission: 'clients:read',
  },
  {
    label: 'Caregivers',
    path: '/caregivers',
    icon: <Users className="h-5 w-5" />,
    section: 'Core',
    permission: 'caregivers:read',
  },
  {
    label: 'Recruitment & HR',
    path: '/recruitment-hr',
    icon: <BriefcaseBusiness className="h-5 w-5" />,
    section: 'Core',
    permission: 'caregivers:read',
  },
  {
    label: 'Branches',
    path: '/branches',
    icon: <Building2 className="h-5 w-5" />,
    section: 'Core',
    permission: 'admin:access',
  },
  {
    label: 'Scheduling',
    path: '/scheduling',
    icon: <Calendar className="h-5 w-5" />,
    section: 'Operations',
    permission: 'visits:read',
  },
  {
    label: 'Calendar View',
    path: '/scheduling/calendar',
    icon: <Calendar className="h-5 w-5" />,
    section: 'Operations',
    permission: 'visits:read',
  },
  {
    label: 'Schedule Optimizer',
    path: '/schedule-optimizer',
    icon: <Route className="h-5 w-5" />,
    section: 'Operations',
    permission: 'admin:access',
  },
  {
    label: 'Care Plans',
    path: '/care-plans',
    icon: <ClipboardList className="h-5 w-5" />,
    section: 'Care',
    permission: 'care_plans:read',
  },
  {
    label: 'Create Care Plan',
    path: '/care-plans/new',
    icon: <Plus className="h-5 w-5" />,
    section: 'Care',
    permission: 'care_plans:create',
  },
  {
    label: 'Tasks',
    path: '/tasks',
    icon: <CheckSquare className="h-5 w-5" />,
    section: 'Care',
    permission: 'tasks:read',
  },
  {
    label: 'Time Tracking',
    path: '/time-tracking',
    icon: <FileText className="h-5 w-5" />,
    section: 'Operations',
    permission: 'evv:read',
  },
  {
    label: 'Billing',
    path: '/billing',
    icon: <DollarSign className="h-5 w-5" />,
    section: 'Finance',
    permission: 'billing:read',
  },
  {
    label: 'Carer Pay',
    path: '/carer-payroll',
    icon: <Calculator className="h-5 w-5" />,
    section: 'Finance',
    permission: 'admin:access',
  },
  {
    label: 'Fleet Costs',
    path: '/fleet',
    icon: <Car className="h-5 w-5" />,
    section: 'Finance',
    requiredRoles: ['SUPER_ADMIN', 'ADMIN', 'REGISTERED_MANAGER', 'DEPUTY_MANAGER', 'MANAGER', 'ACCOUNTANT', 'CARER', 'DRIVER'],
  },
  {
    label: 'Live Carers',
    path: '/live-carers',
    icon: <MapPin className="h-5 w-5" />,
    section: 'Operations',
    permission: 'admin:access',
  },
  {
    label: 'Assistant AI',
    path: '/admin-ai',
    icon: <Sparkles className="h-5 w-5" />,
    section: 'Intelligence',
    permission: 'admin:access',
  },
  {
    label: 'Reports',
    path: '/analytics/reports',
    icon: <BarChart3 className="h-5 w-5" />,
    section: 'Intelligence',
    permission: 'admin:access',
  },
  {
    label: 'Automations',
    path: '/automations',
    icon: <BellRing className="h-5 w-5" />,
    section: 'Intelligence',
    permission: 'admin:access',
  },
  {
    label: 'Admin Guard',
    path: '/admin-error-guard',
    icon: <ShieldCheck className="h-5 w-5" />,
    section: 'Governance',
    permission: 'admin:access',
  },
  {
    label: 'Money Savings',
    path: '/money-savings',
    icon: <TrendingDown className="h-5 w-5" />,
    section: 'Intelligence',
    permission: 'admin:access',
  },
  {
    label: 'Documents',
    path: '/documents',
    icon: <FileText className="h-5 w-5" />,
    section: 'Governance',
    permission: 'admin:access',
  },
  {
    label: 'Production Legal',
    path: '/production-legal',
    icon: <ShieldCheck className="h-5 w-5" />,
    section: 'Governance',
    permission: 'admin:access',
  },
  {
    label: 'API Keys',
    path: '/integration-keys',
    icon: <KeyRound className="h-5 w-5" />,
    section: 'System',
    permission: 'admin:access',
    requiredRoles: ['SUPER_ADMIN', 'ADMIN', 'TECHNICAL_ADMIN'],
  },
  {
    label: 'Incidents',
    path: '/incidents',
    icon: <AlertTriangle className="h-5 w-5" />,
    section: 'Governance',
    permission: 'incidents:read',
  },
  {
    label: 'Quality Assurance',
    path: '/quality-assurance',
    icon: <ClipboardCheck className="h-5 w-5" />,
    section: 'Governance',
    permission: 'audits:view',
  },
  {
    label: 'Compliance',
    path: '/compliance',
    icon: <Shield className="h-5 w-5" />,
    section: 'Governance',
    permission: 'compliance:read',
  },
  {
    label: 'Admin',
    path: '/admin',
    icon: <Settings className="h-5 w-5" />,
    section: 'System',
    permission: 'admin:access',
  },
  {
    label: 'Owner Monitor',
    path: '/owner-activity',
    icon: <MonitorUp className="h-5 w-5" />,
    section: 'System',
    permission: 'admin:access',
    requiredRoles: ['SUPER_ADMIN', 'TECHNICAL_ADMIN'],
  },
  {
    label: 'User Credentials',
    path: '/superadmin/users',
    icon: <UserCog className="h-5 w-5" />,
    section: 'SuperAdmin',
    permission: 'admin:access',
    requiredRoles: ['SUPER_ADMIN'],
  },
  {
    label: 'Company Setup',
    path: '/superadmin/company-setup',
    icon: <Palette className="h-5 w-5" />,
    section: 'SuperAdmin',
    permission: 'admin:access',
    requiredRoles: ['SUPER_ADMIN'],
  },
  {
    label: 'Assistant AI Control',
    path: '/assistant-ai-control',
    icon: <Bot className="h-5 w-5" />,
    section: 'System',
    permission: 'admin:access',
    requiredRoles: ['SUPER_ADMIN', 'TECHNICAL_ADMIN'],
  },
  {
    label: 'Readiness',
    path: '/enterprise-readiness',
    icon: <Activity className="h-5 w-5" />,
    section: 'System',
    permission: 'admin:access',
    requiredRoles: ['SUPER_ADMIN', 'TECHNICAL_ADMIN'],
  },
  {
    label: 'Workflows',
    path: '/workflow-builder',
    icon: <GitBranch className="h-5 w-5" />,
    section: 'System',
    permission: 'admin:access',
    requiredRoles: ['SUPER_ADMIN', 'TECHNICAL_ADMIN'],
  },
  {
    label: 'GP Connect',
    path: '/gp-connect',
    icon: <Stethoscope className="h-5 w-5" />,
    section: 'System',
    permission: 'admin:access',
    requiredRoles: ['SUPER_ADMIN', 'TECHNICAL_ADMIN'],
  },
  {
    label: 'Security',
    path: '/security-enterprise',
    icon: <LockKeyhole className="h-5 w-5" />,
    section: 'System',
    permission: 'admin:access',
    requiredRoles: ['SUPER_ADMIN', 'TECHNICAL_ADMIN'],
  },
  {
    label: 'Roles & Access',
    path: '/roles-access',
    icon: <ShieldCheck className="h-5 w-5" />,
    section: 'System',
    permission: 'admin:access',
  },
];

const technicalAdminAllowedPaths = new Set([
  '/',
  '/integration-keys',
  '/assistant-ai-control',
  '/owner-activity',
  '/enterprise-readiness',
  '/workflow-builder',
  '/gp-connect',
  '/security-enterprise',
  '/settings',
]);

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const { logout, user, token } = useAuth();
  const { can } = usePermissions();
  const { t } = useProductUX();
  const companyProfile = useCompanyProfile();

  const filteredNavItems = navItems.filter((item) => {
    const roles = user?.roles || [];
    const isTechnicalAdminOnly = roles.includes('TECHNICAL_ADMIN') && !roles.some((role) => ['SUPER_ADMIN', 'ADMIN'].includes(role));
    if (isTechnicalAdminOnly && !technicalAdminAllowedPaths.has(item.path)) return false;

    if (item.path === '/') return true;
    
    // Check permission if specified
    if (item.permission && !can(item.permission)) return false;

    if (item.requiredRole && user && !user.roles.includes(item.requiredRole)) return false;
    if (item.requiredRoles && user && !item.requiredRoles.some((role) => user.roles.includes(role))) return false;
    
    // Hide admin section from non-admin users
    if (item.path === '/admin' && user) {
      return user.roles.includes('SUPER_ADMIN') || user.roles.includes('ADMIN') || user.roles.includes('COORDINATOR') || user.roles.includes('MANAGER');
    }
    
    return true;
  });

  let lastSection: NavItem['section'] | null = null;

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-slate-950/55 backdrop-blur-sm lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-72 border-r border-slate-200 bg-white/96 shadow-xl shadow-slate-950/5 backdrop-blur-xl dark:border-slate-800 dark:bg-slate-950/96 lg:w-64 lg:shadow-none',
          'transform transition-transform duration-200 ease-in-out lg:translate-x-0',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex h-full flex-col">
          <div className="flex h-24 items-center justify-between border-b border-purple-100 bg-gradient-to-r from-[#fbf7fd] to-white px-4 dark:border-slate-800 dark:from-slate-950 dark:to-slate-900">
            <div className="flex min-w-0 items-center gap-3">
              <div className="grid h-14 w-14 shrink-0 place-items-center rounded-md bg-white shadow-sm ring-1 ring-[#dec6e8] dark:bg-slate-900 dark:ring-slate-800">
                <img src={companyProfile.logoMarkUrl} alt="" className="h-12 w-12 object-contain" />
              </div>
              <div className="min-w-0">
                <img src={companyProfile.logoFullUrl} alt={companyProfile.publicName} className="h-10 w-auto max-w-[170px] object-contain" />
                <p className="mt-1 truncate text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  {t('header.product')}
                </p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} className="lg:hidden" aria-label="Close menu">
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
            {filteredNavItems.map((item) => {
              // Add IDs for tour targeting
              let id: string | undefined;
              if (item.path === '/clients') id = 'clients-nav';
              if (item.path === '/scheduling') id = 'scheduling';
              if (item.path === '/care-plans') id = 'care-plan';
              if (item.path === '/tasks') id = 'tasks';
              if (item.path === '/time-tracking') id = 'evv-status';

              const showSection = item.section !== lastSection;
              lastSection = item.section;

              return (
                <React.Fragment key={item.path}>
                  {showSection && (
                    <div className="px-3 pb-1 pt-4 first:pt-0 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">
                      {t(`section.${item.section}`)}
                    </div>
                  )}
                  <NavLink
                    id={id}
                    to={item.path}
                    onClick={onClose}
                    className={({ isActive }) =>
                      cn(
                        'group flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-semibold transition-all',
                        isActive
                          ? 'active bg-[#6c3a80] text-white shadow-sm shadow-purple-900/20'
                          : 'text-slate-700 hover:bg-slate-100 hover:text-slate-950 dark:text-slate-300 dark:hover:bg-slate-900 dark:hover:text-white'
                      )
                    }
                  >
                    <span className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-slate-100 text-slate-600 group-[.active]:bg-white/15 group-[.active]:text-white dark:bg-slate-900 dark:text-slate-300">
                      {item.icon}
                    </span>
                    <span className="truncate">{t(`nav.${item.label}`)}</span>
                  </NavLink>
                </React.Fragment>
              );
            })}
          </nav>

          {/* Footer */}
          <div className="space-y-2 border-t border-slate-200 p-3 dark:border-slate-800">
            <NavLink
              to="/settings"
              onClick={onClose}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-semibold transition-all',
                  isActive
                    ? 'bg-[#6c3a80] text-white'
                    : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-900'
                )
              }
            >
              <Settings className="h-5 w-5" />
              {t('nav.Settings')}
            </NavLink>
            <button
              onClick={() => {
                if (token) {
                  void fetch('/api/auth/logout', {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}` },
                  }).catch(() => undefined);
                }
                logout();
                onClose();
              }}
              className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-semibold text-slate-700 transition-all hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-900"
            >
              <LogOut className="h-5 w-5" />
              {t('nav.Logout')}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};
